/**
 * Texture URL resolvers and path rewriting utilities
 */

/**
 * Preprocesses texture names:
 * - Extracts filename from absolute paths
 * - Replaces .psd extension with .png
 */
export function preprocessTextureName(textureName: string): string {
  // Extract just the filename if it contains path separators
  let filename =
    textureName.includes('/') || textureName.includes('\\')
      ? textureName.split(/[\\/]/).pop() || textureName
      : textureName;

  // Replace .psd extension with .png
  if (filename.toLowerCase().endsWith('.psd')) {
    filename = filename.replace(/\.psd$/i, '.png');
  }

  return filename;
}

/**
 * Checks if the URL points to a texture file based on extension.
 */
export function isTextureUrl(url: string): boolean {
  const textureExtensions = [
    '.png',
    '.jpg',
    '.jpeg',
    '.tga',
    '.bmp',
    '.gif',
    '.tiff',
    '.tif',
    '.webp',
    '.psd',
  ];
  const lowerUrl = url.toLowerCase();
  return textureExtensions.some((ext) => lowerUrl.endsWith(ext) || lowerUrl.includes(ext + '?'));
}

/**
 * Resolves texture URLs by applying various transformations.
 * This includes converting asset-lab and shared-folder URLs to their download-textures variants.
 * Only texture files are transformed; everything else is returned as is.
 */
export function resolveTextureUrl(url: string, sourceNodeUrl?: string): string {
  // Only transform texture URLs, not other files
  if (!isTextureUrl(url)) {
    console.debug('[Texture resolver] Bypass non-texture URL:', url);
    return url;
  }
  return resolveSiblingFileUrl(url, sourceNodeUrl);
}

/**
 * Resolves the URL of a file lying next to the source node (textures of a 3D model,
 * atlas and pages of a Spine export) to the download-textures endpoint of the current context.
 * Unlike resolveTextureUrl it does not filter by extension (MYUA-17117).
 */
export function resolveSiblingFileUrl(url: string, sourceNodeUrl?: string): string {
  if (sourceNodeUrl === undefined) {
    console.warn('[Sibling resolver] Bypass URL, no sourceNodeUrl provided:', url);
    return url;
  }

  if (url.startsWith('blob:')) {
    console.debug('[Sibling resolver] Bypass blob URL:', url);
    return url;
  }

  const urlObj = new URL(url, window.location.href);

  const current_url = window.location.href;
  // folder preview in creative hub /creative_hub.html#/folder/7570360/7570361
  // -> /api/crhub/fs/download-textures/[fbx_node_id]/[texture_filename]
  if (current_url.indexOf('/folder/') > -1) {
    const nodeId = current_url.split('/folder/')[1].split('/')[1].split('?')[0];
    let textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    textureFilename = preprocessTextureName(textureFilename);
    const path = `/api/crhub/fs/download-textures/${nodeId}/${textureFilename}`;
    const result = `${urlObj.origin}${path}`;
    console.debug(
      '[Sibling resolver] Resolving texture URL from Creative Hub folder preview:',
      url,
      ' -> ',
      result,
    );
    return result;
  }

  // file preview in creative hub /creative_hub.html#/file_preview/7570360/7570361
  // -> /api/crhub/fs/download-textures/[fbx_node_id]/[texture_filename]
  if (current_url.indexOf('/file_preview/') > -1) {
    const nodeId = current_url.split('/file_preview/')[1].split('/')[1].split('?')[0];
    let textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    textureFilename = preprocessTextureName(textureFilename);
    const path = `/api/crhub/fs/download-textures/${nodeId}/${textureFilename}`;
    const result = `${urlObj.origin}${path}`;
    console.debug(
      '[Sibling resolver] Resolving texture URL from Creative Hub file preview:',
      url,
      ' -> ',
      result,
    );
    return result;
  }

  // shared-folder download -> download-textures
  // browser_url: /shared_folder/Xa7d6eDg3mGPMO06RXLe4BpQZbwJVoOwx5aoAD2Ydjkx7Eynl9z5WKra1N4AyzVM/-322273/7570361
  // /api/crhub/fs/sharing/[shre_id]/[texture_filename]
  // -> /api/crhub/fs/sharing/[share_id]/download-textures/[texture_filename]
  if (current_url.indexOf('/shared_folder/') > -1) {
    // sourceNodeUrl: http://localhost:5174/api/crhub/fs/sharing/Xa7d6eDg3mGPMO06RXLe4BpQZbwJVoOwx5aoAD2Ydjkx7Eynl9z5WKra1N4AyzVM/download?node_id=7570361
    const parts = current_url.split('/shared_folder/')[1].split('/');
    const nodeId = new URL(sourceNodeUrl).searchParams.get('node_id');

    if (nodeId === null) {
      console.warn(
        '[Sibling resolver] Shared folder case. Cannot resolve texture URL from shared folder, node_id missing in sourceNodeUrl:',
        url,
      );
      return url;
    }

    const shareId = parts[0];
    let textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    textureFilename = preprocessTextureName(textureFilename);
    const path = `/api/crhub/fs/sharing/${shareId}/download-textures/${nodeId}/${textureFilename}`;
    const result = `${urlObj.origin}${path}`;
    console.debug(
      '[Sibling resolver] Resolving texture URL from shared folder:',
      url,
      ' -> ',
      result,
    );
    return result;
  }

  // asset-lab sharing download -> download-textures
  // sourceNodeUrl: http://localhost:5174/api/asset-lab/sharing/assets/download?asset_ids=7570361&sharing_id=NTczMzU=
  // texture input url: http://localhost:5174/api/asset-lab/sharing/assets/1657188071_1-pibig-info-p-krasivii-serii-fon-1.jpg
  // expected result: https://preprod.adsadvisor.io/api/asset-lab/sharing/assets/download-textures/<asset_id>/<filename>?sharing_id=<sharing_id>

  //http://localhost:5174/api/asset-lab/sharing/assets/download-textures/7570361/1657188071_1-pibig-info-p-krasivii-serii-fon-1.jpg?sharing_id=NTczMzU=
  if (
    urlObj.pathname.includes('api/asset-lab/sharing/assets') &&
    !urlObj.pathname.includes('download')
  ) {
    const sourceUrl = new URL(sourceNodeUrl);
    const assetId = sourceUrl.searchParams.get('asset_ids');
    const sharingId = sourceUrl.searchParams.get('sharing_id');

    if (assetId === null || sharingId === null) {
      console.warn(
        '[Sibling resolver] Asset-lab sharing case. Cannot resolve texture URL, asset_ids or sharing_id missing in sourceNodeUrl:',
        url,
      );
      return url;
    }

    const textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    const processedFilename = preprocessTextureName(textureFilename);
    const path = `/api/asset-lab/sharing/assets/download-textures/${assetId}/${processedFilename}`;
    const result = `${urlObj.origin}${path}?sharing_id=${sharingId}`;
    console.debug(
      '[Sibling resolver] Resolving texture URL from asset-lab sharing:',
      url,
      ' -> ',
      result,
    );
    return result;
  }

  // asset-lab download -> download-textures
  // This handles both explicit API paths and relative filenames from asset-lab context
  // Case 1: sourceNodeUrl has 'api/asset-lab/assets/download/' (explicit API path)
  // Case 2: URL is a relative filename (e.g., "FC_Objects_D.tga") resolved against window.location
  //         Current url: /asset_lab.html?state_id=1976394#/viewer/
  //         SourceNodeUrl: http://localhost:5173/api/asset-lab/assets/download/?asset_ids=1701071
  //         Url: FC_Objects_D.tga -> becomes http://localhost:5173/FC_Objects_D.tga

  const sourceUrl = new URL(sourceNodeUrl);
  if (
    sourceUrl.pathname.includes('api/asset-lab/assets/download/') ||
    urlObj.pathname === `/${urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1)}`
  ) {
    const nodeId = sourceUrl.searchParams.get('asset_ids');

    if (nodeId === null) {
      console.warn(
        '[Sibling resolver] Asset-lab case. Cannot resolve texture URL from asset-lab, asset_ids missing in sourceNodeUrl:',
        url,
      );
      return url;
    }

    // Extract just the filename from the full path (e.g. "MagBandit.png" from "D:/Work/lonely-expeditions-client/Assets/Characters/MainChar/MagNew/Texture/MagBandit.png")
    let textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    textureFilename = preprocessTextureName(textureFilename);
    const path = `/api/asset-lab/assets/download-textures/${nodeId}/${textureFilename}`;
    const result = `${urlObj.origin}${path}`;
    console.debug('[Sibling resolver] Resolving texture URL from asset-lab:', url, ' -> ', result);
    return result;
  }

  // Convert asset-lab download -> download-textures
  if (urlObj.pathname.includes('/download/')) {
    // Extract just the filename from the path (handles both absolute and relative paths)
    let textureFilename = urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
    textureFilename = preprocessTextureName(textureFilename);
    const path =
      urlObj.pathname.substring(0, urlObj.pathname.lastIndexOf('/download/')) +
      `/download-textures/${textureFilename}`;
    const result = `${urlObj.origin}${path}${urlObj.search}`;
    console.debug(
      '[Sibling resolver] General case using download-texture endpoint:',
      url,
      ' -> ',
      result,
    );
    return result;
  }

  console.debug('[Sibling resolver] Unhandled texture URL:', url);
  return url;
}
